/**
 * The rock streams' tracks (see STREAM, stream_plan.md, twin_streams_plan.md).
 * A sphere with stars enough has two streams or more (see makeStreams), each
 * a loop through its own patch of the stars, never nearer another than a
 * band and a gap (TWIN_SPACING), and each running beside a neighbouring one
 * for a stretch, the other way, exactly that far apart: it goes round the
 * other's circles there from outside, each that much bigger, so the two are
 * parallel round the bends as on the straights. Otherwise, one stream.
 *
 * Each stream's track is a closed, smooth
 * loop laid out once from the seed in the global layer, built from circles
 * joined by their tangents. A star on the route is a circle outside its
 * ring (the arc round it a sweep or a glance); between stars, bend circles
 * in the open make hairpins and S-bends; the straights between get a
 * narrows or a gorge now and then. The whole thing is described by arc
 * length: `streamPoint(s)` is where the track is `s` blocks along, and
 * `streamCoord(p)` is how far along and across a world point is.
 *
 * Rocks ride it at STREAM.speed (see rockMotion.ts, pathOrigin): a rock at
 * belt coordinate `u` is at `streamPoint(u + speed * t)`. The belt is cut
 * into segments of about STREAM.segment blocks (see segmentOf), each owned
 * by one simulation.
 */
import { STREAM } from "../constants";
import { clamp, v2, vDist, vLen, type Vec2 } from "../math";
import type { SphereConfig } from "./areas";
import { Rng } from "./rng";
import type { SunDef } from "./types";

/** A circle the track is built round: a star's (its ring and the gap outside it), or a bend in the open. `turn` 1 anticlockwise, -1 clockwise. */
export interface StreamCircle {
  c: Vec2;
  r: number;
  turn: 1 | -1;
  star?: SunDef;
}

/** What a piece of the track is, or what's in it (`shared`: where it runs beside another stream, see StreamRoute.shared). */
export type PieceKind = "sweep" | "glance" | "hairpin" | "bend" | "straight" | "narrows" | "gorge" | "shared";

interface PieceBase {
  /** Where it starts along the track (blocks), and how long it is. */
  s0: number;
  len: number;
  name: string;
  kind: PieceKind;
}

/** An arc of a circle, from angle `a0`, `turn` the way round. */
export interface ArcPiece extends PieceBase {
  arc: true;
  c: Vec2;
  r: number;
  a0: number;
  turn: 1 | -1;
  /** The star it goes round, if it's a sweep or a glance. */
  star?: string;
}

/** A straight. */
export interface LinePiece extends PieceBase {
  arc: false;
  p0: Vec2;
  p1: Vec2;
  dir: Vec2;
}

export type StreamPiece = ArcPiece | LinePiece;

/** A named place on the track: where (s of its start, or its middle for a narrows or gorge) and what. */
export interface StreamPlace {
  s: number;
  len: number;
  name: string;
  kind: PieceKind;
  /** The star it's at, for a sweep or a glance. */
  star?: string;
}

/** A static rock lining the track, for the cell it falls in to make (see generateCell): a kerb on the outside of a bend, a wall of a narrows, a gorge's rock, or a lone rock to stand on. */
export interface BankSpec {
  at: Vec2;
  radius: number;
  kind: "kerb" | "wall" | "gorge" | "lone";
  seed: number;
  /** A gorge's channel (see AsteroidDef.channel). */
  channel?: { a: Vec2; b: Vec2; half: number };
}

/** Where a stream runs beside another (see twin_streams_plan.md): from `s` for `len` along this one, `STREAM.twin.gap` band edge to band edge from stream `with`, which flows the other way. */
export interface SharedStretch {
  s: number;
  len: number;
  with: number;
  name: string;
}

export interface StreamRoute {
  /** The sphere it's a stream of (see sphereKey). */
  key: string;
  /** Which of the sphere's streams it is (its index in WorldDef.streams). */
  id: number;
  /** "the Verrin Stream". */
  name: string;
  /** Where it runs beside another stream, the other way (none on a sphere with one stream). */
  shared: SharedStretch[];
  pieces: StreamPiece[];
  /** A lap, in blocks. */
  length: number;
  /** Every named place, in order along the track. */
  places: StreamPlace[];
  /** Which way the ring of each star on the route turns (1 anticlockwise), by sun id. */
  turns: Map<string, 1 | -1>;
  /** Where the band pinches (see bandWidth): s of the middle of each narrows. */
  narrows: number[];
  banks: BankSpec[];
  /** How many segments the belt is cut into, and how much belt each owns. */
  segments: number;
  segmentLength: number;
  /** The tightest bend's radius (how hard the inner edge of the band is squeezed, see streamSqueeze). */
  minRadius: number;
}

/** What a stream's global layer is keyed by: the same sphere makes the same track (see generateGlobal). */
export const sphereKey = (sphere: Pick<SphereConfig, "seed" | "radius" | "areaSize">): string => `${sphere.seed}:${sphere.radius}:${sphere.areaSize}`;

/** The sphere a key stands for (its id isn't part of generation). */
export function sphereOfKey(key: string): SphereConfig {
  const [seed, radius, areaSize] = key.split(":").map(Number);
  return { id: "world", seed: seed!, radius: radius!, areaSize: areaSize! };
}

const TAU = Math.PI * 2;
const mod = (a: number, m: number) => ((a % m) + m) % m;
const left = (u: Vec2): Vec2 => v2(-u.y, u.x);
const cross = (a: Vec2, b: Vec2) => a.x * b.y - a.y * b.x;
/** How close two stretches of track far apart along it may come (centre line to centre line): both corridors and a gap between. */
const SELF_CLEAR = STREAM.corridor * 2 + 200;

// ------------------------------------------------------------------ names

const ONSETS = ["", "b", "br", "c", "d", "dr", "f", "g", "gr", "h", "k", "kr", "l", "m", "n", "p", "r", "s", "st", "t", "th", "v", "vr", "w", "z"];
const VOWELS = ["a", "e", "i", "o", "u", "ae", "ia", "io", "ei", "ar", "or"];
const CODAS = ["", "n", "r", "l", "s", "th", "sh", "ck", "m", "rd", "st", "x", "v", "rn", "ll", "nd"];

/** A name from the seed ("Verrin", "Thiock"), unlike any in `taken`. */
export function makeName(rng: Rng, taken: Set<string>): string {
  for (let tries = 0; tries < 50; tries++) {
    const n = rng.next() < 0.6 ? 2 : 3;
    let w = "";
    for (let i = 0; i < n; i++) w += rng.pick(ONSETS) + rng.pick(VOWELS) + (i === n - 1 || rng.next() < 0.3 ? rng.pick(CODAS) : "");
    if (w.length < 3 || w.length > 9) continue;
    const name = w[0]!.toUpperCase() + w.slice(1);
    if (taken.has(name)) continue;
    taken.add(name);
    return name;
  }
  const name = `Star ${taken.size + 1}`;
  taken.add(name);
  return name;
}

// -------------------------------------------------------------- the track

/** How far apart two streams' centre lines are along a shared stretch, and the least they ever are: a band and the gap (see STREAM.twin). */
export const TWIN_SPACING = STREAM.band + STREAM.twin.gap;

/** What every stream of a sphere is laid by. */
interface Ctx {
  sphere: SphereConfig;
  key: string;
  /** How far out the centre line may go. */
  limit: number;
  /** How far each star's ring reaches. */
  ringOuter: (sun: SunDef) => number;
  /** How near a star's middle the centre line may come: outside its ring, the gap and half the band. */
  exclusion: (sun: SunDef) => number;
  /** Every star (every track keeps out of every ring). */
  suns: readonly SunDef[];
  /** The stars a track can go round (those not too near the shell). */
  stars: SunDef[];
  /** How many more loops may be tried, laying several streams (see STREAM.twin.budget): however the stars fall, it's given up on in a while. */
  budget: { left: number };
}

/** A track laid out, not yet finished (see finish): its circles, the pieces chained round them, and which circle each piece is of (its arc, or the straight leaving it). */
interface Laid {
  circles: StreamCircle[];
  pieces: StreamPiece[];
  owner: number[];
  length: number;
  /** The stream it was laid beside (its index), for one laid beside another. */
  twin?: number;
}

type Track = Pick<StreamRoute, "pieces" | "length">;

/**
 * Lay out the track for a sphere with one stream: the stars (with
 * `ringOuter`, how far each one's ring reaches) are visited in a loop that
 * twists through the sphere, bends are added in the open between them, and
 * the result is checked (inside the shell, clear of every star's ring,
 * never crossing itself). Tried again with other rolls until it's good;
 * the last resort is the plain loop of the stars with no bends.
 */
export function makeStreamRoute(sphere: SphereConfig, suns: readonly SunDef[], ringOuter: (sun: SunDef) => number, taken: Set<string>): StreamRoute {
  return makeStreams(sphere, suns, ringOuter, taken, "single")[0]!;
}

/**
 * Lay out a sphere's streams (see twin_streams_plan.md): one for about every
 * STREAM.twin.perStream stars, each a loop through its own patch of them,
 * and each run beside a neighbouring one for a stretch, the other way, a
 * short hop apart; never nearer each other than that anywhere. Where the
 * stars won't make two, one stream by every star, as makeStreamRoute lays
 * it. `mode` "single" lays only the one, "many" only several (none, where
 * they can't be laid).
 */
export function makeStreams(sphere: SphereConfig, suns: readonly SunDef[], ringOuter: (sun: SunDef) => number, taken: Set<string>, mode: "any" | "single" | "many" = "any"): StreamRoute[] {
  const limit = sphere.radius * 0.88 - STREAM.shellGap;
  const exclusion = (sun: SunDef) => ringOuter(sun) + STREAM.ringGap + STREAM.band / 2;
  const ctx: Ctx = { sphere, key: sphereKey(sphere), limit, ringOuter, exclusion, suns, stars: suns.filter((s) => vLen(s.position) + exclusion(s) < limit), budget: { left: STREAM.twin.budget } };
  const many = mode === "single" ? null : layMany(ctx);
  if (!many && mode === "many") return [];
  return finishAll(ctx, many ?? [laySingle(ctx)], taken);
}

/** One stream by every star it can reach (see makeStreamRoute). */
function laySingle(ctx: Ctx): Laid {
  const { sphere, suns, stars, exclusion, limit } = ctx;
  for (let attempt = 0; attempt < 60; attempt++) {
    const rng = new Rng((sphere.seed ^ 0x57e3a) + attempt * 7919);
    const tourCircles = tour(ctx, stars, rng, attempt < 40);
    if (!tourCircles) continue;
    const circles = attempt < 48 ? addBends(tourCircles, suns, exclusion, rng, limit) : tourCircles;
    const made = chain(circles);
    if (made && valid(made.pieces, circles, suns, exclusion, limit)) return { circles, ...made };
  }
  // A sphere with no star in reach of the track: a plain loop round the middle.
  const r = Math.max(400, Math.min(limit - 100, sphere.radius * 0.35));
  const circles: StreamCircle[] = [{ c: v2(r * 0.5, 0), r: r * 0.5, turn: 1 }, { c: v2(-r * 0.5, 0), r: r * 0.5, turn: 1 }];
  return { circles, ...chain(circles)! };
}

/**
 * Several streams, or null where the stars won't make them: as many as the
 * stars allow (fewer if need be, never fewer than two), the stars split into
 * that many patches of neighbours (see splits), each with at least
 * STREAM.twin.minStars, every star of it linked to two others of it (see
 * links), and laid in turn (see layGroups).
 */
function layMany(ctx: Ctx): Laid[] | null {
  const T = STREAM.twin;
  const obstacles = ctx.suns.map((s) => ({ p: s.position, e: ctx.exclusion(s) }));
  for (let n = Math.floor(ctx.stars.length / T.perStream); n >= 2; n--) {
    for (const groups of splits(ctx.stars, n, T.splits)) {
      if (groups.some((g) => g.length < T.minStars)) continue;
      // Every star of each patch must be linked to two of its own (see links), or there's no loop through it.
      if (groups.some((g) => !links(g.map((s) => ({ p: s.position, e: ctx.exclusion(s) })), obstacles, 0, T.slack))) continue;
      const laid = layGroups(ctx, groups);
      if (laid) return laid;
    }
  }
  return null;
}

/**
 * Ways to split the stars into `n` patches of neighbours, best first, at
 * most `most`: k-means from stars as far apart as can be (the furthest out
 * first); for two, also the stars either side of a line through their
 * middle, the line turned a step at a time, cut evenly or one star either
 * way; then k-means from each other star in turn.
 */
function splits(stars: readonly SunDef[], n: number, most: number): SunDef[][][] {
  const out: SunDef[][][] = [];
  const seen = new Set<string>();
  const add = (groups: SunDef[][]) => {
    if (out.length >= most || groups.some((g) => !g.length)) return;
    const key = groups.map((g) => g.map((s) => s.id).sort().join(",")).sort().join("|");
    if (seen.has(key)) return;
    seen.add(key);
    out.push(groups);
  };
  const byFar = [...stars].sort((a, b) => vLen(b.position) - vLen(a.position));
  add(kmeans(stars, n, byFar[0]!));
  if (n === 2) {
    const mid = meanOf(stars.map((s) => s.position));
    for (const shift of [0, -1, 1]) {
      for (let k = 0; k < 16; k++) {
        const a = (k * Math.PI) / 16;
        const along = (s: SunDef) => (s.position.x - mid.x) * Math.cos(a) + (s.position.y - mid.y) * Math.sin(a);
        const sorted = [...stars].sort((p, q) => along(p) - along(q));
        const cut = Math.floor(sorted.length / 2) + shift;
        add([sorted.slice(0, cut), sorted.slice(cut)]);
      }
    }
  }
  for (const first of byFar.slice(1)) add(kmeans(stars, n, first));
  // Patches grown from stars picked at random, a neighbour at a time each in turn, so every one hangs together.
  const rng = new Rng(0x5b17 + stars.length);
  for (let k = 0; k < most * 4 && out.length < most; k++) add(grow(stars, n, rng));
  return out;
}

/** The stars in `n` patches grown from as many picked at random: in turn, the smallest takes a star beside it (one grid step, or diagonal) not yet taken. */
function grow(stars: readonly SunDef[], n: number, rng: Rng): SunDef[][] {
  const pos = stars.map((s) => s.position);
  const nearest = Math.min(...pos.map((p, i) => Math.min(...pos.filter((_, j) => j !== i).map((q) => vDist(p, q)))));
  const owner = new Array<number>(stars.length).fill(-1);
  const groups: number[][] = [];
  while (groups.length < n) {
    const i = rng.int(0, stars.length - 1);
    if (owner[i]! >= 0) continue;
    owner[i] = groups.length;
    groups.push([i]);
  }
  for (let left = stars.length - n; left > 0; ) {
    const order = groups.map((g, i) => i).sort((a, b) => groups[a]!.length - groups[b]!.length || rng.next() - 0.5);
    let took = false;
    for (const g of order) {
      const free = pos.map((p, j) => j).filter((j) => owner[j]! < 0 && groups[g]!.some((i) => vDist(pos[i]!, p_(j)) <= nearest * 1.5));
      if (!free.length) continue;
      const j = free[rng.int(0, free.length - 1)]!;
      owner[j] = g;
      groups[g]!.push(j);
      left--;
      took = true;
      break;
    }
    if (!took) break;
  }
  return groups.map((g) => g.map((i) => stars[i]!));
  function p_(j: number) {
    return pos[j]!;
  }
}

/** The stars in `n` groups by k-means, started from `first` and the stars furthest from those already chosen. */
function kmeans(stars: readonly SunDef[], n: number, first: SunDef): SunDef[][] {
  let centres: Vec2[] = [first.position];
  const apart = (p: Vec2) => Math.min(...centres.map((c) => vDist(c, p)));
  while (centres.length < n) centres.push(stars.reduce((b, s) => (apart(s.position) > apart(b.position) ? s : b)).position);
  let groups: SunDef[][] = [];
  for (let it = 0; it < 30; it++) {
    groups = centres.map(() => []);
    for (const s of stars) {
      let best = 0;
      centres.forEach((c, i) => {
        if (vDist(c, s.position) < vDist(centres[best]!, s.position)) best = i;
      });
      groups[best]!.push(s);
    }
    const next = groups.map((g, i) => (g.length ? meanOf(g.map((s) => s.position)) : centres[i]!));
    const still = next.every((c, i) => vDist(c, centres[i]!) < 1e-6);
    centres = next;
    if (still) break;
  }
  return groups;
}

const meanOf = (ps: readonly Vec2[]): Vec2 => v2(ps.reduce((n, p) => n + p.x, 0) / ps.length, ps.reduce((n, p) => n + p.y, 0) / ps.length);

/**
 * A stream through each group of stars, or null: the first laid on its own
 * (see loops), then each of the others beside the nearest already laid (see
 * twins), neighbour by neighbour, each with a stretch for every one to be
 * laid beside it; where one can't be laid, the one before it is laid
 * another way. Each keeps its bends to its own patch (or to
 * ground already laid, well clear of the streams there), so it leaves the
 * others room.
 */
function layGroups(ctx: Ctx, groups: SunDef[][]): Laid[] | null {
  const tree = treeOf(groups);
  const laid: (Laid | undefined)[] = groups.map(() => undefined);
  const groupOf = new Map(groups.flatMap((g, i) => g.map((s) => [s.id, i] as const)));
  // Whose ground a point is on: the group of the star nearest it.
  const groundOf = (p: Vec2): number => {
    let best = -1;
    let near = Infinity;
    for (const s of ctx.stars) {
      const d = vDist(s.position, p);
      if (d < near) [near, best] = [d, groupOf.get(s.id)!];
    }
    return best;
  };
  const place = (k: number): boolean => {
    if (k === tree.length) return true;
    const { g, parent } = tree[k]!;
    const others = laid.filter((l): l is Laid => l !== undefined);
    const bendOk = (c: Vec2, r: number) => {
      const at = groundOf(c);
      return at === g || (laid[at] !== undefined && others.every((o) => streamCoord(o, c).d > r + TWIN_SPACING + STREAM.corridor));
    };
    const ways = parent < 0 ? loops(ctx, groups[g]!, { others, bendOk, tries: STREAM.twin.tries[0] }) : twins(ctx, groups[g]!, laid[parent]!, parent, others, bendOk);
    // The groups to be laid beside this one: it must have a stretch each can run beside, or it's no use.
    const children = tree.filter((t) => t.parent === g).map((t) => groups[t.g]!.map((s) => s.position));
    let tried = 0;
    for (const way of ways) {
      if (children.some((c) => !stretches(ctx, way, c).length)) continue;
      laid[g] = way;
      if (place(k + 1)) return true;
      if (++tried >= (parent < 0 ? 4 : 2)) break;
    }
    laid[g] = undefined;
    return false;
  };
  return place(0) ? (laid as Laid[]) : null;
}

/** The order the groups are laid in: the first, then always the group nearest one already laid (star to star), beside which it's laid. */
function treeOf(groups: readonly SunDef[][]): { g: number; parent: number }[] {
  const gap = (a: number, b: number) => Math.min(...groups[a]!.flatMap((s) => groups[b]!.map((t) => vDist(s.position, t.position))));
  const out = [{ g: 0, parent: -1 }];
  const done = new Set([0]);
  while (out.length < groups.length) {
    let best = { a: 0, b: 0, d: Infinity };
    for (const a of done) {
      for (let b = 0; b < groups.length; b++) {
        if (done.has(b)) continue;
        const d = gap(a, b);
        if (d < best.d) best = { a, b, d };
      }
    }
    out.push({ g: best.b, parent: best.a });
    done.add(best.b);
  }
  return out;
}

interface LoopOptions {
  /** The streams already laid, which it must keep TWIN_SPACING from. */
  others: readonly Laid[];
  /** May a bend of radius r go at c? */
  bendOk: (c: Vec2, r: number) => boolean;
  tries: number;
  salt?: number;
  /** A run of circles it must go round first, end to end (a stretch beside another stream, and the bends onto and off it). */
  fixed?: StreamCircle[];
  /** The stream it's laid beside: it must run beside it for STREAM.twin.minShared at least. */
  twin?: Laid;
}

/**
 * Loops through every star of a group, as many good ones as turn up in
 * `tries` rolls: by every star (see everyStar), after `fixed` if it's
 * given, bends added in the open (every fourth roll none), and checked as
 * the one stream is (see valid), and against the streams already laid.
 */
function* loops(ctx: Ctx, stars: readonly SunDef[], o: LoopOptions): Generator<Laid> {
  const { sphere, suns, exclusion, limit } = ctx;
  const own = Math.round(stars[0]!.position.x * 3 + stars[0]!.position.y * 7);
  for (let attempt = 0; attempt < o.tries && ctx.budget.left-- > 0; attempt++) {
    const rng = new Rng((sphere.seed ^ 0x57e3a) + (attempt + (o.salt ?? 0)) * 7919 + own);
    const tourCircles = everyCircle(ctx, stars, rng, o.fixed);
    if (!tourCircles) continue;
    const circles = attempt % 4 === 3 ? tourCircles : addBends(tourCircles, suns, exclusion, rng, limit, o.fixed?.length ?? 0, o.bendOk);
    const made = chain(circles);
    if (!made || !valid(made.pieces, circles, suns, exclusion, limit, 150, 75) || !rough(made, o.others) || !valid(made.pieces, circles, suns, exclusion, limit) || !clearOf(made, o.others)) continue;
    if (o.twin) {
      const run = sharedRun(made, o.twin);
      if (Math.min(run.mine.len, run.theirs.len) < STREAM.twin.minShared) continue;
    }
    yield { circles, ...made };
  }
}

/** A quick look before the close one (see clearOf), every 150 blocks: TWIN_SPACING from the other streams. */
function rough(track: Track, others: readonly Track[]): boolean {
  for (const { p } of routePolyline(track, 150)) for (const o of others) if (streamCoord(o, p).d < TWIN_SPACING - 1) return false;
  return true;
}

/**
 * Loops through a group of stars beside an already laid stream (`parent`,
 * stream `parentId`): along each stretch of it that will do (see
 * stretches), nearest the group's middle first, with bends onto and off it
 * of a few sizes (see leads).
 */
function* twins(ctx: Ctx, stars: readonly SunDef[], parent: Laid, parentId: number, others: readonly Laid[], bendOk: (c: Vec2, r: number) => boolean): Generator<Laid> {
  const T = STREAM.twin;
  const runs = stretches(ctx, parent, stars.map((s) => s.position)).slice(0, T.candidates);
  for (const [i, run] of runs.entries()) {
    // Bends onto and off it, tight or wide, a short straight or a longer one from it: the first two that fit.
    const ways = T.lead.flatMap((lead) => STREAM.bend.radius.map((radius) => leads(ctx, run, lead, radius))).filter((w) => w !== null);
    for (const [j, fixed] of ways.slice(0, 2).entries()) {
      for (const way of loops(ctx, stars, { others, bendOk, tries: T.tries[1], salt: 1000 + i * 97 + j * 13, fixed, twin: parent })) yield { ...way, twin: parentId };
    }
  }
}

/** A stretch of a laid track another can run beside: its circles in order along it, with the angle each one's arc starts at and how far round it goes. */
interface Stretch {
  circles: { c: Vec2; r: number; a0: number; sweep: number }[];
  /** Which way the track goes round them all (the same way round each). */
  turn: 1 | -1;
}

/**
 * The stretches of a laid track another can run beside (see
 * twin_streams_plan.md, "Laying them"): runs of its circles each gone round
 * the same way, their middles on the far side from the star of `towards`
 * (the other's stars) nearest the run's middle, so the other goes round
 * every one from outside, TWIN_SPACING out; STREAM.twin.minShared to
 * maxShared long; nearest that star first, the longer first among those
 * about as near.
 */
function stretches(ctx: Ctx, parent: Laid, towards: readonly Vec2[]): Stretch[] {
  const T = STREAM.twin;
  const n = parent.circles.length;
  const arcs: (ArcPiece | undefined)[] = new Array(n).fill(undefined);
  const lines = new Array<number>(n).fill(0);
  parent.pieces.forEach((p, i) => {
    if (p.arc) arcs[parent.owner[i]!] = p;
    else lines[parent.owner[i]!] = p.len;
  });
  const found: { st: Stretch; score: number }[] = [];
  for (let i = 0; i < n; i++) {
    const turn = parent.circles[i]!.turn;
    const first = arcs[i];
    if (!first) continue;
    let len = 0;
    for (let m = 1; m < n - 1; m++) {
      const j = (i + m - 1) % n;
      const arc = arcs[j];
      if (!arc || parent.circles[j]!.turn !== turn) break;
      len += arc.len + (m > 1 ? lines[(j + n - 1) % n]! : 0);
      if (len > T.maxShared) break;
      // Its middle, and which side of it the other's ground is: it must be the side away from the circles' middles.
      const { p, dir } = streamPoint(parent, first.s0 + len / 2);
      const to = towards.reduce((b, q) => (vDist(q, p) < vDist(b, p) ? q : b));
      const side = cross(dir, v2(to.x - p.x, to.y - p.y)) > 0 ? 1 : -1;
      if (side !== -turn || len < T.minShared) continue;
      // The other goes TWIN_SPACING outside it all along: that must be inside the shell and clear of every star's ring.
      let fits = true;
      for (let s = first.s0; fits && s <= first.s0 + len; s += 60) {
        const q = streamPoint(parent, s);
        const o = v2(q.p.x - q.dir.y * side * TWIN_SPACING, q.p.y + q.dir.x * side * TWIN_SPACING);
        fits = vLen(o) <= ctx.limit && ctx.suns.every((sun) => vDist(sun.position, o) >= ctx.exclusion(sun) - 1);
      }
      if (!fits) continue;
      const circles = [];
      for (let k = 0; k < m; k++) {
        const a = arcs[(i + k) % n]!;
        circles.push({ c: a.c, r: a.r, a0: a.a0, sweep: a.len / a.r });
      }
      found.push({ st: { circles, turn }, score: vDist(p, to) - len * 0.25 });
    }
  }
  return found.sort((a, b) => a.score - b.score).map((f) => f.st);
}

/**
 * The circles a stream goes round to run beside a stretch of another (see
 * Stretch), in the order it comes to them: the stretch's own circles the
 * other way round, each TWIN_SPACING bigger, so it runs exactly that far
 * from the other all along, round the bends as on the straights; and a bend
 * of radius `r` at each end, `lead` along a straight off it, that it comes
 * on and goes off round. It joins and leaves a little way inside the arcs
 * at the ends, so it's never nearer the other than on the stretch. Null if
 * the end arcs are too short for that.
 */
function leads(ctx: Ctx, st: Stretch, lead: number, r: number): StreamCircle[] | null {
  const t = st.turn;
  const first = st.circles[0]!;
  const last = st.circles[st.circles.length - 1]!;
  // How far inside each end arc it joins (radians): a fifth of the arc, within limits.
  const inset = (sweep: number) => Math.min(0.15, Math.max(0.03, sweep * 0.2));
  if (first.sweep < inset(first.sweep) * (st.circles.length === 1 ? 3 : 1.5) || last.sweep < inset(last.sweep) * 1.5) return null;
  const R = (c: { r: number }) => c.r + TWIN_SPACING;
  // Where on an end circle (at angle a) it joins or leaves, and the bend `lead` back along the straight it comes in on (`back` 1) or on along the one it leaves by (-1), on the far side from the circle.
  const bendAt = (c: { c: Vec2; r: number }, a: number, back: 1 | -1): StreamCircle => {
    const out = v2(Math.cos(a), Math.sin(a));
    // The way the first stream goes there; the second goes the other way.
    const dir = v2(-out.y * t, out.x * t);
    const at = v2(c.c.x + out.x * R(c), c.c.y + out.y * R(c));
    return { c: v2(at.x + dir.x * lead * back + out.x * r, at.y + dir.y * lead * back + out.y * r), r, turn: t };
  };
  // It comes on at the far end of the stretch (the other's last arc, near where that leaves it) and goes off at the near end (near where that comes onto it).
  const on = bendAt(last, last.a0 + t * (last.sweep - inset(last.sweep)), 1);
  const off = bendAt(first, first.a0 + t * inset(first.sweep), -1);
  const along = [...st.circles].reverse().map((c): StreamCircle => ({ c: c.c, r: R(c), turn: (-t) as 1 | -1 }));
  // The bends must be inside the shell and clear of every star's ring.
  const fits = (b: StreamCircle) => vLen(b.c) + b.r <= ctx.limit && ctx.suns.every((sun) => vDist(sun.position, b.c) >= ctx.exclusion(sun) + b.r);
  return fits(on) && fits(off) ? [on, ...along, off] : null;
}

/** Is a track at least TWIN_SPACING from every other stream's centre line all the way round (exactly that along a shared stretch)? */
function clearOf(track: Track, others: readonly Track[]): boolean {
  if (!others.length) return true;
  for (const { p } of routePolyline(track, 30)) for (const o of others) if (streamCoord(o, p).d < TWIN_SPACING - 1) return false;
  return true;
}

/** Where track `a` runs beside track `b` (TWIN_SPACING apart, the other way): the longest such stretch, as where it starts along each and how long it is along each. */
function sharedRun(a: Track, b: Track): { mine: { s: number; len: number }; theirs: { s: number; len: number } } {
  const step = 10;
  const pts = routePolyline(a, step);
  const on = pts.map(({ p }) => Math.abs(streamCoord(b, p).d - TWIN_SPACING) < 1.5);
  const n = on.length;
  const none = { mine: { s: 0, len: 0 }, theirs: { s: 0, len: 0 } };
  const off = on.indexOf(false);
  if (off < 0) return none;
  let best = { start: 0, count: 0 };
  let start = 0;
  let count = 0;
  for (let k = 1; k <= n; k++) {
    const i = (off + k) % n;
    if (!on[i]) {
      count = 0;
      continue;
    }
    if (!count) start = i;
    count++;
    if (count > best.count) best = { start, count };
  }
  if (best.count < 2) return none;
  const s = pts[best.start]!.s;
  const len = (best.count - 1) * step;
  // b goes the other way: it comes to the end of a's stretch first.
  const t0 = streamCoord(b, streamPoint(a, s + len).p).s;
  const t1 = streamCoord(b, streamPoint(a, s).p).s;
  return { mine: { s, len }, theirs: { s: t0, len: mod(t1 - t0, b.length) } };
}

/** A star or a circle a loop is to go by: where, and how far from it the track runs. */
interface TourNode {
  p: Vec2;
  e: number;
}

/**
 * Which stars the track visits, in order, each as the circle it goes round:
 * every star, by a loop through them all that never crosses itself, where
 * one can be found (`all`, see everyStar); else some of them (see
 * someStars). One star makes a loop out to a far circle and back (a sweep
 * and a hairpin), or round it alone where there's no room for that; two, a
 * loop round both.
 */
function tour(ctx: Ctx, stars: readonly SunDef[], rng: Rng, all: boolean): StreamCircle[] | null {
  const { exclusion, limit } = ctx;
  if (!stars.length) return null;
  const circle = (star: SunDef, turn: 1 | -1): StreamCircle => ({ c: star.position, r: exclusion(star), turn, star });
  if (stars.length <= 2) {
    const [a, b] = stars;
    const first = circle(a!, 1);
    if (b) return [first, circle(b, 1)];
    // A far circle out toward the shell, as far as there's room for: a sweep round the star, a straight out, a hairpin and a straight back.
    for (let tries = 0; tries < 24; tries++) {
      // As big a far circle as fits: in a small sphere, down to not much more than half the band (the inside edge squeezed, see streamSqueeze).
      const r = STREAM.bend.radius[1] - (STREAM.bend.radius[1] - STREAM.band * 0.95) * Math.min(1, tries / 16);
      const ang = rng.range(0, TAU);
      const d = first.r + rng.range(1800, 2600);
      const far = v2(a!.position.x + Math.cos(ang) * d, a!.position.y + Math.sin(ang) * d);
      // Pulled in to fit the shell, if it must be, as long as the straights keep some length.
      const over = vLen(far) + r - limit;
      const pulled = over > 0 ? d - over : d;
      if (pulled < first.r + r + STREAM.corridor) continue;
      const c = v2(a!.position.x + Math.cos(ang) * pulled, a!.position.y + Math.sin(ang) * pulled);
      if (vLen(c) + r <= limit) return [first, { c, r, turn: 1 }];
    }
    // No room for that (a small sphere, a wide ring): once round the star, just outside its ring.
    return [first];
  }
  const order =
    all &&
    everyStar(
      stars.map((s) => ({ p: s.position, e: exclusion(s) })),
      ctx.suns.map((s) => ({ p: s.position, e: exclusion(s) })),
      rng,
    );
  const picked = order ? order.map((i) => stars[i]!) : someStars(stars, exclusion, rng);
  return circlesOf(picked.map((star) => ({ p: star.position, star })), exclusion);
}

/**
 * Every star of `stars` in an order a loop can go round them (see
 * everyStar), each as the circle it's gone round; after `fixed` (circles it
 * must go round first, end to end), if given. Null if no such order turns
 * up, or two circles side by side are too close to join.
 */
function everyCircle(ctx: Ctx, stars: readonly SunDef[], rng: Rng, fixed: readonly StreamCircle[] = []): StreamCircle[] | null {
  // A fixed run (a stretch beside another stream, a bend onto it and one off) stands where it's gone along: its ends at their bends' middles, the
  // circles between at the side of them facing the ends (where it goes round them), not their middles on the other stream's side.
  const m = fixed.length;
  const toward = m > 1 ? meanOf([fixed[0]!.c, fixed[m - 1]!.c]) : undefined;
  const along = fixed.map((c, i): TourNode => {
    if (i === 0 || i === m - 1 || !toward) return { p: c.c, e: c.r };
    const d = vDist(toward, c.c) || 1;
    return { p: v2(c.c.x + ((toward.x - c.c.x) / d) * c.r, c.c.y + ((toward.y - c.c.y) / d) * c.r), e: 0 };
  });
  const nodes: TourNode[] = [...along, ...stars.map((s) => ({ p: s.position, e: ctx.exclusion(s) }))];
  const order = everyStar(
    nodes,
    ctx.suns.map((s) => ({ p: s.position, e: ctx.exclusion(s) })),
    rng,
    fixed.length,
    STREAM.twin.slack,
    STREAM.twin.search,
  );
  if (!order) return null;
  return circlesOf(
    order.map((i) => (i < fixed.length ? { p: nodes[i]!.p, fixed: fixed[i]! } : { p: nodes[i]!.p, star: stars[i - fixed.length]! })),
    ctx.exclusion,
  );
}

/**
 * The circles of a loop through `stops`, in order: a fixed circle as it is,
 * and each star gone round the way the loop turns at it (anticlockwise at a
 * convex corner of the loop, clockwise at a reflex one). Null where two side
 * by side (not both fixed) are too close to join.
 */
function circlesOf(stops: { p: Vec2; star?: SunDef; fixed?: StreamCircle }[], exclusion: (sun: SunDef) => number): StreamCircle[] | null {
  const n = stops.length;
  const out: StreamCircle[] = [];
  for (let i = 0; i < n; i++) {
    const stop = stops[i]!;
    if (stop.fixed) {
      out.push(stop.fixed);
      continue;
    }
    const p = stops[(i + n - 1) % n]!.p;
    const q = stop.p;
    const r = stops[(i + 1) % n]!.p;
    const turn: 1 | -1 = cross(v2(q.x - p.x, q.y - p.y), v2(r.x - q.x, r.y - q.y)) >= 0 ? 1 : -1;
    out.push({ c: stop.star!.position, r: exclusion(stop.star!), turn, star: stop.star });
  }
  // Neighbours whose circles meet can't be joined: no track this time.
  for (let i = 0; i < n; i++) {
    if (stops[i]!.fixed && stops[(i + 1) % n]!.fixed) continue;
    const a = out[i]!;
    const b = out[(i + 1) % n]!;
    if (vDist(a.c, b.c) < a.r + b.r + STREAM.corridor * 2) return null;
  }
  return out;
}

/**
 * Every node, in an order the track can go round them without crossing
 * itself (as indices), or null if none is found in a while: a randomised
 * search, step by step to a neighbouring one (near enough to be the next
 * along, across a dark reach if need be, with no star's ring of
 * `obstacles` by the way between them), the one with the fewest ways on
 * first, and never across a stretch already laid. The first `fixed` nodes
 * are a run it starts with as they are, and comes back to the first of at
 * the end; the rest of that run it never goes to.
 */
function everyStar(nodes: readonly TourNode[], obstacles: readonly TourNode[], rng: Rng, fixed = 0, slack = 1, search = 20_000): number[] | null {
  const nbrs = links(nodes, obstacles, fixed, slack);
  if (!nbrs) return null;
  const n = nodes.length;
  const pos = nodes.map((x) => x.p);
  const path = fixed ? Array.from({ length: fixed }, (_, i) => i) : [rng.int(0, n - 1)];
  const used = new Array<boolean>(n).fill(false);
  for (const i of path) used[i] = true;
  let budget = search;
  // Would the stretch from a to b cross one already laid (other than those it meets end to end)?
  const crosses = (a: number, b: number) => {
    for (let k = 0; k + 1 < path.length; k++) {
      const p = path[k]!;
      const q = path[k + 1]!;
      if (p === a || p === b || q === a || q === b) continue;
      if (segmentsCross(pos[a]!, pos[b]!, pos[p]!, pos[q]!)) return true;
    }
    return false;
  };
  const step = (): boolean => {
    if (--budget < 0) return false;
    const last = path[path.length - 1]!;
    if (path.length === n) return nbrs[last]!.includes(path[0]!) && !crosses(last, path[0]!);
    const options = nbrs[last]!
      .filter((j) => !used[j] && !crosses(last, j))
      .map((j) => ({ j, k: nbrs[j]!.filter((x) => !used[x]).length + rng.next() }))
      .sort((p, q) => p.k - q.k);
    for (const { j } of options) {
      used[j] = true;
      path.push(j);
      if (step()) return true;
      path.pop();
      used[j] = false;
    }
    return false;
  };
  return step() ? path : null;
}

/**
 * Which nodes can be next to which along a loop (see everyStar): near enough
 * to be the next along (out to three steps on the grid of stars), with no
 * star's ring of `obstacles` by the way between them; only the ends of the
 * first `fixed` (a run gone along as it is) link to anything, and those
 * without the check for rings by the way (valid makes it). Null if some
 * node can't be gone through (fewer than two links), or the run's ends
 * link to nothing.
 */
function links(nodes: readonly TourNode[], obstacles: readonly TourNode[], fixed: number, slack = 1): number[][] | null {
  const n = nodes.length;
  const pos = nodes.map((x) => x.p);
  const free = pos.slice(fixed);
  const pool = free.length >= 2 ? free : pos;
  const nearest = Math.min(...pool.map((p, i) => Math.min(...pool.filter((_, j) => j !== i).map((q) => vDist(p, q)))));
  const end = (i: number) => i >= fixed || i === 0 || i === fixed - 1;
  const same = (a: Vec2, b: Vec2) => a.x === b.x && a.y === b.y;
  const nbrs: number[][] = pos.map(() => []);
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (!end(i) || !end(j) || j < fixed) continue;
      // Out to three steps on the grid of stars: over a dark reach (a cell with no star) as well as to the stars beside.
      if (vDist(pos[i]!, pos[j]!) > nearest * 3.1) continue;
      // The track runs off to one side of the line between them by as much as the circle it's leaving or coming to (tapering from one to the other):
      // no other star's ring may be that close.
      const ei = nodes[i]!.e;
      const ej = nodes[j]!.e;
      // From the end of a fixed run (a bend by another stream) the way out is checked when the track's laid (see valid), not here.
      if (
        i >= fixed &&
        obstacles.some((o) => {
          if (same(o.p, pos[i]!) || same(o.p, pos[j]!)) return false;
          const { d, t } = segmentNear(o.p, pos[i]!, pos[j]!);
          return d < o.e + (ei * (1 - t) + ej * t) * slack;
        })
      )
        continue;
      nbrs[i]!.push(j);
      nbrs[j]!.push(i);
    }
  }
  for (let i = fixed; i < n; i++) if (nbrs[i]!.length < 2) return null;
  if (fixed && (!nbrs[0]!.length || !nbrs[fixed - 1]!.length)) return null;
  return nbrs;
}

/** How far point p is from the segment a to b (`d`), and how far along it (0 at a, 1 at b) the nearest point is (`t`). */
function segmentNear(p: Vec2, a: Vec2, b: Vec2): { d: number; t: number } {
  const d = v2(b.x - a.x, b.y - a.y);
  const len2 = d.x * d.x + d.y * d.y;
  const t = len2 ? Math.max(0, Math.min(1, ((p.x - a.x) * d.x + (p.y - a.y) * d.y) / len2)) : 0;
  return { d: vDist(p, v2(a.x + d.x * t, a.y + d.y * t)), t };
}

/** Do segments a to b and c to d cross (touching ends don't count)? */
function segmentsCross(a: Vec2, b: Vec2, c: Vec2, d: Vec2): boolean {
  const o = (p: Vec2, q: Vec2, r: Vec2) => Math.sign(cross(v2(q.x - p.x, q.y - p.y), v2(r.x - p.x, r.y - p.y)));
  return o(a, b, c) * o(a, b, d) < 0 && o(c, d, a) * o(c, d, b) < 0;
}

/**
 * Some of the stars, in order: sorted by angle round the sphere's middle
 * they make a loop that never crosses itself, and picking by turns from the
 * outer and inner ones makes it zigzag between the shell and the middle.
 */
function someStars(stars: readonly SunDef[], exclusion: (sun: SunDef) => number, rng: Rng): SunDef[] {
  const R = Math.max(...stars.map((s) => vLen(s.position)));
  const middle = stars.filter((s) => vLen(s.position) < 1);
  const round = stars
    .filter((s) => vLen(s.position) >= 1)
    .map((s) => ({ s, a: Math.atan2(s.position.y, s.position.x), far: vLen(s.position) > R * 0.5 }))
    .sort((p, q) => p.a - q.a);
  // Start somewhere round, so different attempts pick different stars.
  const start = rng.int(0, round.length - 1);
  const picked: SunDef[] = [];
  let wantFar = rng.next() < 0.5;
  for (let i = 0; i < round.length; i++) {
    const { s, far } = round[(start + i) % round.length]!;
    const take = far === wantFar ? rng.next() < 0.95 : rng.next() < 0.5;
    if (!take) continue;
    const prev = picked[picked.length - 1];
    if (prev && vDist(prev.position, s.position) < exclusion(prev) + exclusion(s) + SELF_CLEAR) continue;
    picked.push(s);
    if (rng.next() < 0.7) wantFar = !wantFar;
  }
  if (picked.length < 3) {
    for (const { s } of round) if (!picked.includes(s) && picked.length < 3) picked.push(s);
    picked.sort((p, q) => Math.atan2(p.position.y, p.position.x) - Math.atan2(q.position.y, q.position.x));
  }
  // Back through the middle now and then: the middle star goes between two of its neighbours.
  if (middle.length && rng.next() < 0.7) picked.splice(rng.int(1, picked.length - 1), 0, middle[0]!);
  return picked;
}

/**
 * Bend circles in the open between stars (see STREAM.bend): one or two on
 * each long enough gap, off to one side of the line between the stars (two
 * to opposite sides, for an S), gone round the far way so the track bulges
 * out round them. Only where there's room: inside the shell, clear of
 * every star's ring, and where `ok` allows; never between two of the first
 * `fixed` circles (a run gone round as it is).
 */
function addBends(circles: StreamCircle[], suns: readonly SunDef[], exclusion: (sun: SunDef) => number, rng: Rng, limit: number, fixed = 0, ok: (c: Vec2, r: number) => boolean = () => true): StreamCircle[] {
  const B = STREAM.bend;
  const out: StreamCircle[] = [];
  const n = circles.length;
  for (let i = 0; i < n; i++) {
    const a = circles[i]!;
    const b = circles[(i + 1) % n]!;
    out.push(a);
    if (i + 1 < fixed) continue;
    const d = v2(b.c.x - a.c.x, b.c.y - a.c.y);
    const D = vLen(d);
    // A long gap always gets bends; a shorter one, usually.
    if (D < B.room || (D < B.room * 1.8 && rng.next() >= B.chance)) continue;
    const u = v2(d.x / D, d.y / D);
    const l = left(u);
    const bends = D >= B.room * 3 ? 3 : D >= B.room * 1.6 && rng.next() < 0.5 ? 2 : 1;
    const ts = bends === 3 ? [rng.range(0.2, 0.3), rng.range(0.43, 0.57), rng.range(0.7, 0.8)] : bends === 2 ? [rng.range(0.27, 0.4), rng.range(0.6, 0.73)] : [rng.range(0.36, 0.64)];
    let side: 1 | -1 = rng.next() < 0.5 ? 1 : -1;
    for (const t of ts) {
      const r = rng.range(B.radius[0], B.radius[1]);
      const off = r * rng.range(B.offset[0], B.offset[1]);
      const c = v2(a.c.x + u.x * t * D + l.x * off * side, a.c.y + u.y * t * D + l.y * off * side);
      const fits =
        vLen(c) + r < limit &&
        suns.every((s) => vDist(s.position, c) > exclusion(s) + r + STREAM.corridor * 2) &&
        vDist(c, a.c) > a.r + r + STREAM.corridor * 2 &&
        vDist(c, b.c) > b.r + r + STREAM.corridor * 2 &&
        ok(c, r);
      // Displaced to the left of the way we're going, it's gone round clockwise (its far side), and the other way about.
      if (fits) out.push({ c, r, turn: side > 0 ? -1 : 1 });
      side = side > 0 ? -1 : 1;
    }
  }
  return out;
}

/**
 * The track through a loop of circles: the tangent line from each to the
 * next (an outer tangent between circles gone round the same way, a
 * crossing one between circles gone round opposite ways), and on each
 * circle the arc from where the line before touches to where the next one
 * leaves (one circle alone: all the way round it). With which circle each
 * piece is of (`owner`: its arc, or the line leaving it). Null if a pair of
 * circles can't be joined.
 */
function chain(circles: StreamCircle[]): { pieces: StreamPiece[]; owner: number[]; length: number } | null {
  const n = circles.length;
  if (n === 1) {
    // Round one circle and back to the start: a single arc, all the way round.
    const C = circles[0]!;
    return { pieces: [{ arc: true, c: C.c, r: C.r, a0: 0, turn: C.turn, s0: 0, len: TAU * C.r, name: "", kind: C.star ? "glance" : "bend", star: C.star?.id }], owner: [0], length: TAU * C.r };
  }
  if (n < 2) return null;
  const lines: { p0: Vec2; p1: Vec2 }[] = [];
  for (let i = 0; i < n; i++) {
    const P = circles[i]!;
    const Q = circles[(i + 1) % n]!;
    const d = v2(Q.c.x - P.c.x, Q.c.y - P.c.y);
    const D = vLen(d);
    if (D < 1e-6) return null;
    // The line's direction: the circles' edges, each on the side its turn puts it, must lie on it.
    const k = (Q.r * Q.turn - P.r * P.turn) / D;
    if (Math.abs(k) >= 1) return null;
    const psi = Math.atan2(d.y, d.x) - Math.asin(k);
    const u = v2(Math.cos(psi), Math.sin(psi));
    const l = left(u);
    lines.push({ p0: v2(P.c.x - P.r * P.turn * l.x, P.c.y - P.r * P.turn * l.y), p1: v2(Q.c.x - Q.r * Q.turn * l.x, Q.c.y - Q.r * Q.turn * l.y) });
  }
  const pieces: StreamPiece[] = [];
  const owner: number[] = [];
  for (let i = 0; i < n; i++) {
    const C = circles[i]!;
    const into = lines[(i + n - 1) % n]!.p1;
    const outOf = lines[i]!.p0;
    const a0 = Math.atan2(into.y - C.c.y, into.x - C.c.x);
    const a1 = Math.atan2(outOf.y - C.c.y, outOf.x - C.c.x);
    const da = mod(C.turn * (a1 - a0), TAU);
    if (da * C.r > 1) {
      pieces.push({ arc: true, c: C.c, r: C.r, a0, turn: C.turn, s0: 0, len: da * C.r, name: "", kind: C.star ? "glance" : "bend", star: C.star?.id });
      owner.push(i);
    }
    const { p0, p1 } = lines[i]!;
    const len = vDist(p0, p1);
    if (len > 1) {
      pieces.push({ arc: false, p0, p1, dir: v2((p1.x - p0.x) / len, (p1.y - p0.y) / len), s0: 0, len, name: "", kind: "straight" });
      owner.push(i);
    }
  }
  let s = 0;
  for (const p of pieces) {
    p.s0 = s;
    s += p.len;
  }
  return { pieces, owner, length: s };
}

/** Points along the track every `step` blocks (the last a step or less before the first again). */
export function routePolyline(route: Track, step: number): { p: Vec2; s: number }[] {
  const out: { p: Vec2; s: number }[] = [];
  for (let s = 0; s < route.length; s += step) out.push({ p: streamPoint(route, s).p, s });
  return out;
}

/**
 * Is the track good? Inside the shell everywhere; clear of every star's
 * ring but on the arc round that star; and never near itself (two stretches
 * closer than SELF_CLEAR, more than twice that apart along it). Looked at
 * every `step` blocks; with `slack`, failed only where it fails by more
 * than that (a quick look, to throw out the hopeless before the close one).
 */
function valid(pieces: StreamPiece[], circles: StreamCircle[], suns: readonly SunDef[], exclusion: (sun: SunDef) => number, limit: number, step = 30, slack = 0): boolean {
  const length = pieces.reduce((n, p) => n + p.len, 0);
  const route = { pieces, length };
  const samples = routePolyline(route, step);
  const clear = SELF_CLEAR - slack;
  const away = suns.map((sun) => exclusion(sun) - 1 - slack);
  for (const { p, s } of samples) {
    if (vLen(p) > limit + slack) return false;
    const piece = pieceAt(route, s);
    for (let i = 0; i < suns.length; i++) {
      const sun = suns[i]!;
      if (piece.arc && piece.star === sun.id) continue;
      if (vDist(sun.position, p) < away[i]!) return false;
    }
    // Not inside a bend circle it isn't going round (the arcs and their tangents touch the circle, nothing else comes near).
    for (const c of circles) {
      if (c.star || (piece.arc && piece.c === c.c)) continue;
      if (vDist(c.c, p) < c.r - 1 - slack && !(piece.arc === false && lineTouches(piece, c))) return false;
    }
  }
  // Nearness to itself, through a grid of the samples.
  const cell = SELF_CLEAR;
  const grid = new Map<number, number[]>();
  const keyAt = (cx: number, cy: number) => (cx + 4096) * 8192 + cy + 4096;
  const keyOf = (p: Vec2) => keyAt(Math.floor(p.x / cell), Math.floor(p.y / cell));
  samples.forEach(({ p }, i) => {
    const k = keyOf(p);
    const list = grid.get(k);
    if (list) list.push(i);
    else grid.set(k, [i]);
  });
  // (Hot: along the track first, then squared distances.)
  const clear2 = clear * clear;
  for (let i = 0; i < samples.length; i++) {
    const { p, s } = samples[i]!;
    const cx = Math.floor(p.x / cell);
    const cy = Math.floor(p.y / cell);
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const list = grid.get(keyAt(cx + dx, cy + dy));
        if (!list) continue;
        for (const j of list) {
          if (j <= i) continue;
          const q = samples[j]!;
          const ds = Math.abs(q.s - s);
          if (Math.min(ds, length - ds) <= SELF_CLEAR * 2) continue;
          const ex = q.p.x - p.x;
          const ey = q.p.y - p.y;
          if (ex * ex + ey * ey < clear2) return false;
        }
      }
    }
  }
  return true;
}

/** Does a straight touch a circle (it's the tangent into or out of it)? */
function lineTouches(line: LinePiece, c: StreamCircle): boolean {
  return Math.abs(vDist(line.p0, c.c) - c.r) < 1 || Math.abs(vDist(line.p1, c.c) - c.r) < 1;
}

/**
 * Finish the sphere's streams: where each twin runs beside the one it was
 * laid beside (on both, named: "the Twins", or a name each where there are
 * several), each stream finished (see finish), and no bank left where
 * another stream runs or in the gap beside it.
 */
function finishAll(ctx: Ctx, laid: Laid[], taken: Set<string>): StreamRoute[] {
  const shared: SharedStretch[][] = laid.map(() => []);
  const pairs = laid.flatMap((l, i) => (l.twin === undefined ? [] : [{ i, j: l.twin }]));
  const nameRng = new Rng(ctx.sphere.seed ^ 0x7a1e5);
  for (const { i, j } of pairs) {
    const run = sharedRun(laid[i]!, laid[j]!);
    let name = "the Twins";
    if (pairs.length > 1) name = `the ${makeName(nameRng, taken)} Twins`;
    else taken.add(name);
    shared[i]!.push({ s: run.mine.s, len: run.mine.len, with: j, name });
    shared[j]!.push({ s: run.theirs.s, len: run.theirs.len, with: i, name });
  }
  const routes = laid.map((l, id) => finish(ctx, id, l, shared[id]!, taken));
  if (routes.length > 1) {
    for (const r of routes) r.banks = r.banks.filter((b) => routes.every((o) => o === r || streamCoord(o, b.at).d >= TWIN_SPACING + b.radius * 1.25));
  }
  return routes;
}

/** Does [a, a + la) overlap [b, b + lb) on a loop of length L? */
const overlaps = (a: number, la: number, b: number, lb: number, L: number) => mod(a - b, L) < lb || mod(b - a, L) < la;

/**
 * Finish a track: name it and its pieces (a sweep or a glance at each star
 * by how far round it goes, a hairpin or a bend in the open), mark where it
 * runs beside another, put a narrows or a gorge on some of the long
 * straights (none beside another stream: the bands stay full width side by
 * side), line it with banks, and cut it into segments.
 */
function finish(ctx: Ctx, id: number, laid: Laid, shared: SharedStretch[], taken: Set<string>): StreamRoute {
  const { sphere, key } = ctx;
  const { pieces, circles, length } = laid;
  const rng = new Rng((sphere.seed ^ 0x9a3e1) + id * 0x2f1b3);
  const route: StreamRoute = { key, id, name: "", shared, pieces, length, places: [], turns: new Map(), narrows: [], banks: [], segments: 0, segmentLength: 0, minRadius: Infinity };
  for (const c of circles) if (c.star) route.turns.set(c.star.id, c.turn);
  const starName = (sid: string | undefined) => circles.find((c) => c.star?.id === sid)?.star?.name ?? "a star";
  const beside = (s0: number, len: number) => shared.some((sh) => overlaps(s0, len, sh.s, sh.len, length));
  for (const p of pieces) {
    if (p.arc) {
      route.minRadius = Math.min(route.minRadius, p.r);
      const turns = p.len / (TAU * p.r);
      if (p.star) {
        p.kind = turns >= STREAM.sweepTurn ? "sweep" : "glance";
        p.name = `the ${p.kind === "sweep" ? "Sweep" : "Glance"} at ${starName(p.star)}`;
      } else {
        p.kind = turns >= STREAM.hairpinTurn ? "hairpin" : "bend";
        // A kink too slight to notice isn't a place.
        if (turns < 0.08) continue;
        p.name = `${makeName(rng, taken)} ${p.kind === "hairpin" ? "Hairpin" : "Bend"}`;
      }
      route.places.push({ s: p.s0, len: p.len, name: p.name, kind: p.kind, star: p.star });
    } else {
      p.name = `the ${makeName(rng, taken)} Straight`;
      route.places.push({ s: p.s0, len: p.len, name: p.name, kind: "straight" });
      const N = STREAM.narrows;
      const G = STREAM.gorge;
      const mid = p.s0 + p.len / 2;
      if (beside(p.s0, p.len)) continue;
      if (p.len >= G.minStraight && rng.next() < G.chance) {
        const name = `${makeName(rng, taken)} Gorge`;
        route.places.push({ s: mid, len: 0, name, kind: "gorge" });
        const radius = Math.round(rng.range(G.radius[0], G.radius[1]));
        const at = streamPoint(route, mid).p;
        const reach = radius * 1.25 + 30;
        route.banks.push({ at, radius, kind: "gorge", seed: rng.int(1, 0x7fffffff), channel: { a: v2(-p.dir.x * reach, -p.dir.y * reach), b: v2(p.dir.x * reach, p.dir.y * reach), half: STREAM.band / 2 + 20 } });
      } else if (p.len >= N.minStraight && rng.next() < N.chance) {
        route.places.push({ s: mid, len: N.length, name: `the ${makeName(rng, taken)} Narrows`, kind: "narrows" });
        route.narrows.push(mid);
      }
    }
  }
  for (const sh of shared) route.places.push({ s: sh.s, len: sh.len, name: sh.name, kind: "shared" });
  route.places.sort((a, b) => a.s - b.s);
  route.name = `the ${makeName(rng, taken)} Stream`;
  if (!Number.isFinite(route.minRadius)) route.minRadius = STREAM.bend.radius[0];
  route.segments = Math.max(1, Math.round(length / STREAM.segment));
  route.segmentLength = length / route.segments;
  banks(route, rng, sphere, ctx.suns.map((s) => ({ c: s.position, r: ctx.ringOuter(s) })));
  return route;
}

/** The static rocks lining the track (see BankSpec), clear of the shell and of every star's ring (`rings`: each one's middle and how far it reaches). */
function banks(route: StreamRoute, rng: Rng, sphere: SphereConfig, rings: { c: Vec2; r: number }[]): void {
  const limit = sphere.radius * 0.88 - 60;
  // Clear of the shell, and of every ring: the bank's grid (a square, however the ring runs past it) past the ring rocks' grids.
  const fits = (at: Vec2, radius: number) => vLen(at) + radius * 1.25 < limit && rings.every((g) => vDist(g.c, at) > g.r + (radius * 1.25 + 20) * 1.42 + 50);
  const put = (at: Vec2, radius: number, kind: BankSpec["kind"]) => {
    if (fits(at, radius)) route.banks.push({ at: v2(Math.round(at.x), Math.round(at.y)), radius, kind, seed: rng.int(1, 0x7fffffff) });
  };
  const K = STREAM.kerbs;
  const W = STREAM.narrows;
  const L = STREAM.lone;
  for (const p of route.pieces) {
    if (p.arc) {
      // Kerbs on the outside of a real bend, spaced along it.
      if (p.len / (TAU * p.r) < 0.25) continue;
      const n = Math.floor((p.len * 0.8) / K.every);
      for (let i = 0; i < n; i++) {
        const s = p.s0 + p.len * 0.1 + (i + 0.5) * ((p.len * 0.8) / n);
        const radius = rng.int(K.radius[0], K.radius[1]);
        const { p: q, dir } = streamPoint(route, s);
        // Outside: away from the circle's middle, which is to the left going anticlockwise.
        const out = left(dir);
        const off = -p.turn * (STREAM.corridor + STREAM.bankGap + radius * 1.25);
        put(v2(q.x + out.x * off, q.y + out.y * off), radius, "kerb");
      }
    } else {
      const mids = route.places.filter((pl) => (pl.kind === "narrows" || pl.kind === "gorge") && pl.s > p.s0 && pl.s < p.s0 + p.len);
      for (const m of mids) {
        if (m.kind !== "narrows") continue;
        // Two walls along the pinch.
        for (let s = m.s - W.length / 2; s <= m.s + W.length / 2; s += 2.6 * W.wall[1]) {
          for (const side of [1, -1]) {
            const radius = rng.int(W.wall[0], W.wall[1]);
            const { p: q, dir } = streamPoint(route, s);
            const l = left(dir);
            const off = side * (STREAM.corridor + radius * 1.25);
            put(v2(q.x + l.x * off, q.y + l.y * off), radius, "wall");
          }
        }
      }
      // Lone rocks to stand on, either side by turns, away from the ends and from whatever's in the middle.
      let side = rng.next() < 0.5 ? 1 : -1;
      for (let s = p.s0 + L.every * 0.6; s < p.s0 + p.len - 200; s += L.every * rng.range(0.8, 1.3)) {
        if (mids.some((m) => Math.abs(m.s - s) < 420)) continue;
        const radius = rng.int(L.radius[0], L.radius[1]);
        const { p: q, dir } = streamPoint(route, s);
        const l = left(dir);
        const off = side * (STREAM.corridor + STREAM.bankGap + radius * 1.25 + rng.range(0, 40));
        put(v2(q.x + l.x * off, q.y + l.y * off), radius, "lone");
        side = -side as 1 | -1;
      }
    }
  }
}

// -------------------------------------------------------------- geometry

/** The piece of the track `s` blocks along (s wrapped to a lap). */
export function pieceAt(route: Pick<StreamRoute, "pieces" | "length">, s: number): StreamPiece {
  const u = mod(s, route.length);
  const pieces = route.pieces;
  let lo = 0;
  let hi = pieces.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (pieces[mid]!.s0 <= u) lo = mid;
    else hi = mid - 1;
  }
  return pieces[lo]!;
}

/** Where the track's centre line is `s` blocks along, and which way it goes there. */
export function streamPoint(route: Pick<StreamRoute, "pieces" | "length">, s: number): { p: Vec2; dir: Vec2; piece: StreamPiece } {
  const u = mod(s, route.length);
  const piece = pieceAt(route, u);
  const t = u - piece.s0;
  if (piece.arc) {
    const a = piece.a0 + (piece.turn * t) / piece.r;
    return { p: v2(piece.c.x + Math.cos(a) * piece.r, piece.c.y + Math.sin(a) * piece.r), dir: v2(-Math.sin(a) * piece.turn, Math.cos(a) * piece.turn), piece };
  }
  return { p: v2(piece.p0.x + piece.dir.x * t, piece.p0.y + piece.dir.y * t), dir: piece.dir, piece };
}

/** The nearest point of one piece to `p`: how far along the track, how far across (left of the way it goes positive), and the distance. */
function nearestOn(piece: StreamPiece, p: Vec2): { s: number; o: number; d: number } {
  if (piece.arc) {
    const dx = p.x - piece.c.x;
    const dy = p.y - piece.c.y;
    const phi = Math.atan2(dy, dx);
    const da = piece.len / piece.r;
    const delta = mod(piece.turn * (phi - piece.a0), TAU);
    if (delta <= da) {
      const q = v2(piece.c.x + Math.cos(phi) * piece.r, piece.c.y + Math.sin(phi) * piece.r);
      const dir = v2(-Math.sin(phi) * piece.turn, Math.cos(phi) * piece.turn);
      const d = vDist(p, q);
      return { s: piece.s0 + delta * piece.r, o: cross(dir, v2(p.x - q.x, p.y - q.y)), d };
    }
    // Past either end: the nearer end.
    const a1 = piece.a0 + piece.turn * da;
    const e0 = v2(piece.c.x + Math.cos(piece.a0) * piece.r, piece.c.y + Math.sin(piece.a0) * piece.r);
    const e1 = v2(piece.c.x + Math.cos(a1) * piece.r, piece.c.y + Math.sin(a1) * piece.r);
    const d0 = vDist(p, e0);
    const d1 = vDist(p, e1);
    const [q, s, a] = d0 <= d1 ? [e0, piece.s0, piece.a0] : [e1, piece.s0 + piece.len, a1];
    const dir = v2(-Math.sin(a) * piece.turn, Math.cos(a) * piece.turn);
    return { s, o: cross(dir, v2(p.x - q.x, p.y - q.y)), d: Math.min(d0, d1) };
  }
  const t = clamp((p.x - piece.p0.x) * piece.dir.x + (p.y - piece.p0.y) * piece.dir.y, 0, piece.len);
  const q = v2(piece.p0.x + piece.dir.x * t, piece.p0.y + piece.dir.y * t);
  return { s: piece.s0 + t, o: cross(piece.dir, v2(p.x - q.x, p.y - q.y)), d: vDist(p, q) };
}

/** How far a piece's nearest point can be from `p`, at least (a box round the piece). */
function atLeast(piece: StreamPiece, p: Vec2): number {
  const [x0, y0, x1, y1] = piece.arc ? [piece.c.x - piece.r, piece.c.y - piece.r, piece.c.x + piece.r, piece.c.y + piece.r] : [Math.min(piece.p0.x, piece.p1.x), Math.min(piece.p0.y, piece.p1.y), Math.max(piece.p0.x, piece.p1.x), Math.max(piece.p0.y, piece.p1.y)];
  return Math.hypot(Math.max(x0 - p.x, 0, p.x - x1), Math.max(y0 - p.y, 0, p.y - y1));
}

/** Where a world point is in the stream's frame: `s` blocks along the track (the nearest point of its centre line), `o` across it (left of the way it goes positive), and `d` its distance from the line. */
export function streamCoord(route: Pick<StreamRoute, "pieces" | "length">, p: Vec2): { s: number; o: number; d: number } {
  let best = { s: 0, o: 0, d: Infinity };
  for (const piece of route.pieces) {
    if (atLeast(piece, p) >= best.d) continue;
    const near = nearestOn(piece, p);
    if (near.d < best.d) best = near;
  }
  return best;
}

/** How far a world point is from the track's centre line (Infinity with no stream). What danger and riches go by (see stream_plan.md). */
export function streamDistance(route: Pick<StreamRoute, "pieces" | "length"> | undefined, p: Vec2): number {
  return route ? streamCoord(route, p).d : Infinity;
}

/** The stream nearest a world point, and where the point is in its frame (see streamCoord). Undefined with no stream. */
export function nearestStream<R extends Pick<StreamRoute, "pieces" | "length">>(streams: readonly R[] | undefined, p: Vec2): { route: R; s: number; o: number; d: number } | undefined {
  let best: { route: R; s: number; o: number; d: number } | undefined;
  for (const route of streams ?? []) {
    const c = streamCoord(route, p);
    if (!best || c.d < best.d) best = { route, ...c };
  }
  return best;
}

/** How far a world point is from the nearest stream's centre line (Infinity with none). What danger and riches go by (see stream_plan.md). */
export function streamsDistance(streams: readonly Pick<StreamRoute, "pieces" | "length">[] | undefined, p: Vec2): number {
  return nearestStream(streams, p)?.d ?? Infinity;
}

/** The shared stretch `s` is in, if it's in one (see StreamRoute.shared). */
export function sharedAt(route: Pick<StreamRoute, "shared" | "length">, s: number): SharedStretch | undefined {
  return route.shared.find((sh) => mod(s - sh.s, route.length) < sh.len);
}

/** How wide the band is `s` blocks along: STREAM.band, pinched at a narrows (see STREAM.narrows). */
export function bandWidth(route: Pick<StreamRoute, "narrows" | "length">, s: number): number {
  const N = STREAM.narrows;
  let w: number = STREAM.band;
  for (const m of route.narrows) {
    const ds = Math.abs(mod(s - m + route.length / 2, route.length) - route.length / 2);
    if (ds <= N.length / 2) w = Math.min(w, N.width);
    else if (ds <= N.length) {
      const k = (ds - N.length / 2) / (N.length / 2);
      w = Math.min(w, N.width + (STREAM.band - N.width) * k * k * (3 - 2 * k));
    }
  }
  return w;
}

/** How much the belt is pushed together along the track on the inside of its tightest bend, for a rock `o` blocks across the band: 1 mid-band, less toward the inside edge. */
export function streamSqueeze(route: Pick<StreamRoute, "minRadius">, o: number): number {
  return (route.minRadius - Math.abs(o)) / route.minRadius;
}

/** Which segment of the belt a belt coordinate is in (see STREAM.segment). */
export function segmentOf(route: Pick<StreamRoute, "segments" | "segmentLength" | "length">, u: number): number {
  return Math.min(route.segments - 1, Math.floor(mod(u, route.length) / route.segmentLength));
}

/** Where on the track a rock at belt coordinate `u` is at time `t` (s). */
export function streamAt(route: Pick<StreamRoute, "pieces" | "length">, u: number, t: number): { p: Vec2; dir: Vec2; piece: StreamPiece; s: number } {
  const s = mod(u + STREAM.speed * t, route.length);
  return { ...streamPoint(route, s), s };
}

/** The places ahead of `s` along the track, nearest first, each with how far on it is (blocks). */
export function placesAhead(route: Pick<StreamRoute, "places" | "length">, s: number): { place: StreamPlace; ahead: number }[] {
  return route.places
    .map((place) => ({ place, ahead: mod(place.s - s, route.length) }))
    .sort((a, b) => a.ahead - b.ahead);
}

/** The next star along the track from `s` (the start of the arc round it), and how far on it is. */
export function nextStar(route: Pick<StreamRoute, "places" | "length">, s: number): { place: StreamPlace; ahead: number } | undefined {
  return placesAhead(route, s).find(({ place }) => place.star);
}

/** The place `s` is in (a stretch beside another stream, an arc, a straight, or the narrows or gorge in a straight). */
export function placeAt(route: Pick<StreamRoute, "places" | "length">, s: number): StreamPlace | undefined {
  const u = mod(s, route.length);
  const beside = route.places.find((p) => p.kind === "shared" && mod(u - p.s, route.length) < p.len);
  if (beside) return beside;
  const feature = route.places.find((p) => (p.kind === "narrows" || p.kind === "gorge") && Math.abs(p.s - u) <= Math.max(p.len, 200) / 2);
  if (feature) return feature;
  let best: StreamPlace | undefined;
  for (const p of route.places) if (p.kind !== "shared" && p.len > 0 && p.s <= u && (!best || p.s > best.s)) best = p;
  return best;
}
